import {
  Box,
  CancelIconButton,
  DeleteIconButton,
  InputLabel,
  Menu,
  MenuItem,
  Stack,
  Typography,
  ValidationErrors,
  clickableStyle,
  formatFileSize,
  getSpacing,
  isFilenameImage,
} from "@compass/core";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import { ReactNode, memo, useEffect, useRef, useState } from "react";
import { DefaultExtensionType, FileIcon as ReactFileIcon, defaultStyles } from "react-file-icon";

const openInBrowserExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "txt", "pdf", "csv"];

export type FilePanelBaseProps = {
  getFile: (open: boolean) => void;
  fileName?: string;
  fileSize?: number;
  fileRef?: string | undefined;
  onFileChange?: (fileName: string, fileSize: number, fileData: string) => void;
  uploadingData?: string | undefined;
  imagePreviewData?: string | undefined;
  errors?: ValidationErrors | string[];
  border?: boolean;
  label?: ReactNode;
  cancel?: () => void;
  delete?: () => void;
  disabled?: boolean;
};

export const FilePanelBase = memo((props: FilePanelBaseProps) => {
  const iconWidth = "35px";

  // in react icon, when the icon's width is 35px, the icon height must be 42px
  // so we set the size to 42px (42x42) for images width and height
  const thumbnailSize = "42px";

  const hasFile = props.fileName ? true : false;
  const border = props.border ?? true;
  const extension: string | undefined = props.fileName?.split(".").pop();
  const canOpenFile = extension ? openInBrowserExtensions.includes(extension) : false;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const isImage = isFilenameImage(props.fileName);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const errorMsg = props.errors ? props.errors?.[0] : null;

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDrop = (e) => {
    setIsDragging(false);
  };

  const handleDragLeave = (e) => {
    if (e.clientX <= 0 || e.clientX >= window.innerWidth || e.clientY <= 0 || e.clientY >= window.innerHeight) {
      setIsDragging(false);
    }
  };

  useEffect(() => {
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("drop", handleDrop);
    window.addEventListener("dragleave", handleDragLeave);

    return () => {
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("drop", handleDrop);
      window.removeEventListener("dragleave", handleDragLeave);
    };
  }, []);

  const handleChange = (e: any) => {
    if (props.onFileChange) {
      let files = e.target.files;

      if (files && files.length > 0) {
        var file = files[0];

        var reader = new FileReader();
        reader.onload = function (loadEvent) {
          props.onFileChange?.(file.name, file.size, loadEvent?.target?.result?.toString()!);
        };

        reader.readAsDataURL(file);
      }
    }
  };

  const fileBody = (
    <Stack direction="row" alignItems="center" justifyContent={"space-between"}>
      <Stack direction="row" spacing="sm" alignItems="center" overflow={"auto"}>
        <Box
          position="relative"
          width={thumbnailSize}
          height={thumbnailSize}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          onClick={(e) => setMenuAnchorEl(e.currentTarget)}
        >
          {isImage ? (
            <>
              {props.uploadingData && <img src={props.uploadingData} style={{ maxWidth: "100%", maxHeight: "100%" }} />}
              {!props.uploadingData && props.fileRef && (
                <img src={props.imagePreviewData} style={{ maxWidth: "100%", maxHeight: "100%" }} />
              )}
            </>
          ) : (
            <Box display="inline-flex" width={iconWidth} height={thumbnailSize}>
              <ReactFileIcon extension={extension} {...defaultStyles[extension as DefaultExtensionType]} />
            </Box>
          )}
        </Box>

        <Stack justifyContent="center" minWidth={0} maxWidth="100%" height="100%">
          <Typography
            variant="inherit"
            title={props.fileName}
            lineHeight={"100%"}
            sx={{
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            {props.fileName}
          </Typography>
          <Typography variant="caption">{formatFileSize(props.fileSize)}</Typography>
        </Stack>
      </Stack>

      <Stack direction="row">
        {props.delete && <DeleteIconButton onClick={() => props.delete?.()} />}
        {props.cancel && <CancelIconButton onClick={() => props.cancel?.()} />}
      </Stack>
    </Stack>
  );

  const noFileBody = (
    <Stack
      position="relative"
      direction="row"
      justifyContent={"center"}
      alignItems={"center"}
      onClick={() => fileInputRef?.current?.click()}
      sx={{ height: thumbnailSize, ...clickableStyle, opacity: "0.7" }}
    >
      {!isDragging ? (
        <>
          <FileUploadRoundedIcon />
          <Typography>File</Typography>
        </>
      ) : (
        <Typography>Drop file here</Typography>
      )}
    </Stack>
  );

  const menu = (
    <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)}>
      {props.fileRef && canOpenFile && (
        <MenuItem
          onClick={() => {
            props.getFile(true);
            setMenuAnchorEl(null);
          }}
        >
          Open
        </MenuItem>
      )}
      {props.fileRef && (
        <MenuItem
          onClick={() => {
            props.getFile(false);
            setMenuAnchorEl(null);
          }}
        >
          Download
        </MenuItem>
      )}
      {props.onFileChange && (
        <MenuItem
          onClick={() => {
            fileInputRef?.current?.click();
            setMenuAnchorEl(null);
          }}
        >
          Re-Upload
        </MenuItem>
      )}
      {/* {props.cancel && (
        <MenuItem
          onClick={() => {
            props.cancel?.();
            setMenuAnchorEl(null);
          }}
        >
          Clear File
        </MenuItem>
      )} */}
    </Menu>
  );

  return (
    <>
      {props.label && <InputLabel shrink={true}>{props.label}</InputLabel>}

      <Box
        position="relative"
        p={getSpacing("sm")}
        overflow="hidden" // need this for ellipsis to work?
        sx={{
          border: border
            ? (theme) => `1px solid ${errorMsg ? theme.palette.error.dark : theme.palette.border.dark}`
            : undefined,
          pointerEvents: props.disabled ? "none" : "auto",
          opacity: props.disabled ? 0.5 : 1,
        }}
      >
        {hasFile ? fileBody : noFileBody}

        {props.onFileChange && (
          <input
            ref={fileInputRef}
            type="file"
            style={{
              display: isDragging ? "inline-block" : "none",
              outline: "1px solid black",
              position: "absolute",
              width: "100%",
              height: "100%",
              top: "0px",
              left: "0px",
              bottom: "0px",
              right: "0px",
              fontSize: "20px",
              opacity: "0",
              ...clickableStyle,

              "&:after": {
                display: "block",
                content: "ha",
              },
            }}
            onChange={(e) => handleChange(e)}
          />
        )}

        {errorMsg && (
          <Box color={(theme) => theme.palette.error.main} sx={{ fontSize: "12px" }} textAlign="center">
            {errorMsg}
          </Box>
        )}
      </Box>

      {menu}
    </>
  );
});
